import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface UseSpeechSynthesisReturn {
  isSupported: boolean;
  isSpeaking: boolean;
  /** Call from a user gesture handler to unlock TTS on Chrome. */
  prime: () => void;
  speak: (text: string, lang?: string) => void;
  stop: () => void;
}

function pickVoice(voices: SpeechSynthesisVoice[], lang: string): SpeechSynthesisVoice | null {
  // Prefer a voice that matches the exact locale (e.g. fr-FR).
  const exact = voices.find((v) => v.lang === lang);
  if (exact) return exact;
  // Fallback to any voice that starts with the language prefix (e.g. fr).
  const prefix = lang.split('-')[0];
  return voices.find((v) => v.lang.startsWith(prefix ?? '')) ?? null;
}

function i18nToSpeechLang(lang: string): string {
  if (lang.startsWith('fr')) return 'fr-FR';
  if (lang.startsWith('en')) return 'en-US';
  return lang;
}

export function useSpeechSynthesis(): UseSpeechSynthesisReturn {
  const { i18n } = useTranslation();
  const [isSpeaking, setIsSpeaking] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const primedRef = useRef(false);

  const isSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  // Cache voices — they load asynchronously on Chrome.
  const voicesRef = useRef<SpeechSynthesisVoice[]>([]);
  useEffect(() => {
    if (!isSupported) return;

    const loadVoices = () => {
      voicesRef.current = window.speechSynthesis.getVoices();
    };

    loadVoices();
    window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
    };
  }, [isSupported]);

  /**
   * Prime speechSynthesis by speaking an empty utterance.
   * Chrome requires the first speak() to happen within a user gesture context.
   * Call this from a click/tap handler (e.g. when the voice overlay opens)
   * to unlock TTS for subsequent async calls.
   */
  const prime = useCallback(() => {
    if (!isSupported || primedRef.current) return;
    primedRef.current = true;
    const empty = new SpeechSynthesisUtterance('');
    empty.volume = 0;
    window.speechSynthesis.speak(empty);
  }, [isSupported]);

  const stop = useCallback(() => {
    if (!isSupported) return;
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  }, [isSupported]);

  const speak = useCallback(
    (text: string, lang?: string) => {
      if (!isSupported || !text.trim()) return;

      window.speechSynthesis.cancel();

      const resolvedLang = lang ?? i18nToSpeechLang(i18n.language);

      // Chrome bug: calling speak() immediately after cancel() silently fails.
      // A 150ms delay lets Chrome's internal queue reset properly.
      const doSpeak = () => {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = resolvedLang;

        const voice = pickVoice(voicesRef.current, resolvedLang);
        if (voice) utterance.voice = voice;

        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = (e) => {
          if (e.error !== 'canceled') {
            setIsSpeaking(false);
          }
        };

        utteranceRef.current = utterance;
        window.speechSynthesis.speak(utterance);

        // Chrome stalled detection: if speaking hasn't started after 300ms,
        // the call was likely blocked. Try once more after a longer delay.
        setTimeout(() => {
          if (!window.speechSynthesis.speaking && utteranceRef.current === utterance) {
            window.speechSynthesis.cancel();
            setTimeout(() => {
              window.speechSynthesis.speak(utterance);
            }, 100);
          }
        }, 300);
      };

      setTimeout(doSpeak, 150);
    },
    [isSupported, i18n.language],
  );

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (isSupported) {
        window.speechSynthesis.cancel();
      }
    };
  }, [isSupported]);

  return { isSupported, isSpeaking, prime, speak, stop };
}
